// Workspace-wide ticket triage for /tickets/general.
//
// Classifies every open task + bug into buckets based on live
// agent_presence + agent_activity_log signal:
//
//   • now          — an agent is actively running on this ticket right now.
//                   Two conditions qualify (either is sufficient):
//                     a) agent_presence.current_ticket_id = ticket.id AND
//                        status='active' AND last_heartbeat within LIVE_WINDOW_MS
//                     b) ticket.state='in_progress' AND ticket.assignee_email
//                        matches an active presence row (heartbeat within
//                        LIVE_WINDOW_MS). Agents rarely set current_ticket_id,
//                        so (b) is the primary signal in practice.
//   • ongoing      — state='in_progress' OR there's a 'picked_up' activity
//                   log row, but no matching live presence (agent was
//                   working but isn't currently heartbeating)
//   • blocked      — state='blocked'; rendered between Ongoing and Upcoming
//                   with blocker ticket IDs from ticket_dependencies.
//   • upcoming     — state IN ('not_started', null) or never picked up
//   • readyToTest  — state='ready_to_test' (QA-pending; right column)
//
// Returns { now: [...], ongoing: [...], blocked: [...], upcoming: [...], readyToTest: [...], agentPresence: [...] }.
// Each entry: { id, external_id, type, title, state, priority,
//   assignee_email, actor, actorDisplay, tsISO, commitSha, parentStory,
//   parentEpic, updatedAt, childTotal, childDone, progressPct,
//   blockedByIds: [...] }.  blockedByIds is populated for blocked tickets.
//
// Performance changes (ticket #4498):
//   - Done/closed tickets excluded from query (reduces payload ~40%)
//   - AbortController timeout (10s) prevents silent stuck-loading
//   - _fetchAllTickets is kept for background refresh only
//   - fetchTicketPage() exported for lazy bucket loading

import { SUPABASE_URL, restHeaders } from '../../../shared/functions/supabaseConfig.js'

const WORKSPACE_ID = '69e5911201b0ef47b675463f'
const LIVE_WINDOW_MS = 10 * 60 * 1000  // presence fresh for 10 min

// Initial page size: 200 tickets covers ~99% of active workspace tasks/bugs.
// This is the FIRST paint batch — UI shows in <800ms on this limit.
// A background refetch loads the full set for completeness.
const INITIAL_LIMIT = 200
// Fetch timeout. After this the fetch rejects → UI shows retry CTA.
const FETCH_TIMEOUT_MS = 10_000

// Map ticket state → progress percentage (used when no sub-tasks exist).
export const STATE_PROGRESS_PCT = {
  not_started: 0,
  blocked: null,       // null = use last non-blocked value with red overlay
  in_progress: 33,
  ready_to_test: 66,
  awaiting_approval: 80,
  done: 100
}

// Wrap a fetch with AbortController timeout.
const _fetchWithTimeout = (url, opts, ms = FETCH_TIMEOUT_MS) => {
  if (typeof AbortController === 'undefined') return fetch(url, opts)
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), ms)
  return fetch(url, { ...opts, signal: ctrl.signal })
    .finally(() => clearTimeout(timer))
}

// Shared query base — excludes done/closed to keep payload small.
// Includes type=decision so ASK-USER / pending-decision tickets appear in the
// decisions bucket on /tickets/general.
const _ticketBase = () =>
  `${SUPABASE_URL}/rest/v1/tickets` +
  `?select=id,external_id,type,title,state,priority,assignee_email,parent_ticket_id,labels,metadata,updated_at,sub_issues_total,sub_issues_completed` +
  `&workspace_id=eq.${WORKSPACE_ID}` +
  `&or=(type.in.(task,bug,user_story,epic,decision),type.is.null)` +
  `&deleted=eq.false` +
  `&state=not.in.(done,closed,CLOSED)` +
  `&order=updated_at.desc`

// Fetch a single page of tickets (used for lazy bucket loading).
// Returns { rows, total }.
export const fetchTicketPage = async (offset = 0, limit = INITIAL_LIMIT) => {
  const r = await _fetchWithTimeout(`${_ticketBase()}&offset=${offset}&limit=${limit}`, {
    headers: { ...restHeaders(), Prefer: 'count=exact' }
  })
  if (!r.ok) return { rows: [], total: 0 }
  const rows = await r.json()
  const cr = r.headers.get('content-range') || ''
  const total = parseInt((cr.split('/')[1] || '0'), 10) || rows.length
  return { rows, total }
}

// Full fetch: initial page + all remaining pages in parallel.
// Used by the shared ticketStore background-refresh path.
const _fetchAllTickets = async () => {
  const PAGE = 1000
  const r0 = await _fetchWithTimeout(`${_ticketBase()}&offset=0&limit=${PAGE}`, {
    headers: { ...restHeaders(), Prefer: 'count=exact' }
  })
  if (!r0.ok) return []
  const page0 = await r0.json()

  const cr = r0.headers.get('content-range') || ''
  const total = parseInt((cr.split('/')[1] || '0'), 10) || page0.length
  if (total <= PAGE) return page0

  const pages = Math.ceil(total / PAGE)
  const rest = await Promise.all(
    Array.from({ length: pages - 1 }, (_, i) =>
      _fetchWithTimeout(`${_ticketBase()}&offset=${(i + 1) * PAGE}&limit=${PAGE}`, { headers: restHeaders() })
        .then(r => r.ok ? r.json() : [])
    )
  )
  return [page0, ...rest].flat()
}

// Core triage computation — shared by fast-path (initial) and full-refresh.
// Takes pre-fetched rows + presence + activity + deps arrays.
// deps: array of { ticket_id, depends_on_ticket_id, kind } rows from ticket_dependencies.
export const computeTriage = (rows, presence, activity, deps = []) => {
  // Index ticket rows for parent lookups.
  const byId = new Map()
  for (const r of rows) byId.set(r.id, r)

  // Build child-task counts per parent ticket id.
  const childCountByParent = new Map()
  const childDoneByParent = new Map()
  for (const r of rows) {
    if (!r.parent_ticket_id) continue
    const effectiveType = r.type || 'task'
    if (effectiveType !== 'task' && effectiveType !== 'bug') continue
    const pid = r.parent_ticket_id
    childCountByParent.set(pid, (childCountByParent.get(pid) || 0) + 1)
    if (r.state === 'done' || r.state === 'closed') {
      childDoneByParent.set(pid, (childDoneByParent.get(pid) || 0) + 1)
    }
  }

  const epicTitleById = new Map()
  for (const r of rows) if (r.type === 'epic') epicTitleById.set(r.id, r.title || '')
  const storyById = new Map()
  for (const r of rows) if (r.type === 'user_story') storyById.set(r.id, r)

  // Walk the parent_ticket_id chain for a given ticket row until an epic is
  // found or the chain ends. Handles non-standard hierarchies (task → task →
  // story → epic) in addition to the canonical (task → story → epic) path.
  // Returns the epic's title string, or '' if no epic is reachable.
  const _resolveEpicTitle = (startRow) => {
    let cur = startRow
    const visited = new Set()
    while (cur && !visited.has(cur.id)) {
      visited.add(cur.id)
      if (cur.type === 'epic') return cur.title || ''
      if (!cur.parent_ticket_id) return ''
      cur = byId.get(cur.parent_ticket_id) || null
    }
    return ''
  }

  // Build live-presence index.
  const now = Date.now()
  const liveAgentByTicket = new Map()
  const livePresenceByEmail = new Map()
  for (const p of presence) {
    if (p.status !== 'active') continue
    const age = p.last_heartbeat ? now - new Date(p.last_heartbeat).getTime() : Infinity
    if (age > LIVE_WINDOW_MS) continue
    if (p.current_ticket_id) {
      liveAgentByTicket.set(p.current_ticket_id, p)
    }
    if (p.assignee_email) livePresenceByEmail.set(p.assignee_email.toLowerCase(), p)
  }

  // Build blockers index: ticket_id → [{ id, external_id, title }] of blocker tickets.
  // deps rows have: { ticket_id, depends_on_ticket_id, kind }
  // A ticket is blocked BY depends_on_ticket_id when kind='blocked_by'.
  const blockersByTicket = new Map()
  for (const d of deps) {
    if (d.kind !== 'blocked_by') continue
    const tid = d.ticket_id
    if (!blockersByTicket.has(tid)) blockersByTicket.set(tid, [])
    const blockerRow = byId.get(d.depends_on_ticket_id)
    blockersByTicket.get(tid).push({
      id: d.depends_on_ticket_id,
      external_id: blockerRow ? blockerRow.external_id : null,
      title: blockerRow ? blockerRow.title : null
    })
  }

  // Most-recent activity per ticket.
  const lastActivityByTicket = new Map()
  for (const a of activity) {
    if (!a.ticket_id) continue
    if (!lastActivityByTicket.has(a.ticket_id)) lastActivityByTicket.set(a.ticket_id, a)
  }

  const buckets = { now: [], ongoing: [], blocked: [], upcoming: [], pendingApproval: [], readyToTest: [], decisions: [] }
  for (const r of rows) {
    const rType = r.type || 'task'
    if (r.state === 'done' || r.state === 'CLOSED' || r.state === 'closed') continue

    // Decision tickets go to their own dedicated bucket regardless of state.
    // They are ASK-USER / pending-decision items and should always be visible.
    if (rType === 'decision') {
      buckets.decisions.push({
        id: r.id, external_id: r.external_id, type: rType, title: r.title,
        state: r.state, priority: r.priority, assignee_email: r.assignee_email,
        labels: r.labels || [],
        parentStoryTitle: r.parent_ticket_id ? (storyById.get(r.parent_ticket_id)?.title || '') : '',
        parentEpicTitle: _resolveEpicTitle(r),
        metadata: r.metadata, updatedAt: r.updated_at
      })
      continue
    }

    // Only task and bug types go into the standard activity buckets.
    if (rType !== 'task' && rType !== 'bug') continue

    if (r.state === 'awaiting_approval') {
      const parentStoryA = r.parent_ticket_id ? storyById.get(r.parent_ticket_id) : null
      buckets.pendingApproval.push({
        id: r.id, external_id: r.external_id, type: rType, title: r.title,
        state: r.state, priority: r.priority, assignee_email: r.assignee_email,
        labels: r.labels || [],
        parentStoryTitle: parentStoryA?.title || '',
        parentEpicTitle: _resolveEpicTitle(r),
        metadata: r.metadata, updatedAt: r.updated_at
      })
      continue
    }

    const parentStory = r.parent_ticket_id ? storyById.get(r.parent_ticket_id) : null
    const parentEpicTitle = _resolveEpicTitle(r)
    const parentStoryTitle = parentStory?.title || ''

    const subTotal = (r.sub_issues_total || 0) + (childCountByParent.get(r.id) || 0)
    const subDone = (r.sub_issues_completed || 0) + (childDoneByParent.get(r.id) || 0)
    const isBlocked = r.state === 'blocked'
    let progressPct
    if (subTotal > 0) {
      progressPct = Math.round((subDone / subTotal) * 100)
    } else {
      const mapped = STATE_PROGRESS_PCT[r.state]
      progressPct = mapped !== null && mapped !== undefined ? mapped : 0
    }

    const blockedByIds = blockersByTicket.get(r.id) || []
    const entry = {
      id: r.id, external_id: r.external_id, type: rType, title: r.title,
      state: r.state, priority: r.priority, assignee_email: r.assignee_email,
      labels: r.labels || [], parentStoryTitle, parentEpicTitle,
      metadata: r.metadata, updatedAt: r.updated_at,
      progressPct, isBlocked, childTotal: subTotal, childDone: subDone,
      blockedByIds
    }

    if (r.state === 'ready_to_test') {
      buckets.readyToTest.push(entry)
      continue
    }

    // Blocked tickets get their own dedicated bucket.
    if (r.state === 'blocked') {
      const lastAct = lastActivityByTicket.get(r.id)
      entry.lastActivityAgent = lastAct ? lastAct.agent_name : null
      entry.lastActivityAt = lastAct ? lastAct.created_at : null
      entry.liveAgent = null
      entry.liveAgentHeartbeat = null
      buckets.blocked.push(entry)
      continue
    }

    let liveAgent = liveAgentByTicket.get(r.id) || null
    if (!liveAgent && r.state === 'in_progress' && r.assignee_email) {
      liveAgent = livePresenceByEmail.get(r.assignee_email.toLowerCase()) || null
    }
    const live = !!liveAgent
    const lastAct = lastActivityByTicket.get(r.id)

    entry.liveAgent = liveAgent ? liveAgent.agent_name : null
    entry.liveAgentHeartbeat = liveAgent ? liveAgent.last_heartbeat : null
    entry.lastActivityAgent = lastAct ? lastAct.agent_name : null
    entry.lastActivityAt = lastAct ? lastAct.created_at : null

    let bucket
    if (live) bucket = 'now'
    else if (r.state === 'in_progress' || (lastAct && lastAct.kind === 'picked_up')) bucket = 'ongoing'
    else bucket = 'upcoming'

    buckets[bucket].push(entry)
  }

  // Sort within buckets.
  buckets.now.sort((a, b) => (new Date(b.liveAgentHeartbeat || 0) - new Date(a.liveAgentHeartbeat || 0)))
  buckets.ongoing.sort((a, b) => (new Date(b.lastActivityAt || b.updatedAt || 0) - new Date(a.lastActivityAt || a.updatedAt || 0)))
  const _priRank = (p) => ({ P0: 0, P1: 1, P2: 2, P3: 3 }[p] ?? 4)
  const _byPriority = (a, b) => {
    const d = _priRank(a.priority) - _priRank(b.priority)
    return d !== 0 ? d : (new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
  }
  buckets.blocked.sort(_byPriority)
  buckets.upcoming.sort(_byPriority)
  buckets.readyToTest.sort(_byPriority)
  // Decisions: most-recent updated first (no priority ranking — they are user decisions)
  buckets.decisions.sort((a, b) => (new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)))

  const lastActivityByAgent = new Map()
  for (const a of activity) {
    if (!a.agent_name) continue
    if (!lastActivityByAgent.has(a.agent_name)) lastActivityByAgent.set(a.agent_name, a)
  }
  const agentPresence = presence.map(p => ({
    ...p, lastActivity: lastActivityByAgent.get(p.agent_name) || null
  }))

  return { ...buckets, agentPresence }
}

// Main export — used by ticketStore and page onCreate.
// Strategy:
//   1. Fetch initial INITIAL_LIMIT tickets + presence + activity in parallel (fast path)
//   2. Compute triage from first page
//   3. If total > INITIAL_LIMIT, caller can request a background full-refresh
export const fetchGeneralTriage = async ({ full = false } = {}) => {
  const [{ rows, total }, presenceRes, activityRes, depsRes] = await Promise.all([
    full ? _fetchAllTickets().then(rows => ({ rows, total: rows.length })) : fetchTicketPage(0, INITIAL_LIMIT),
    _fetchWithTimeout(
      `${SUPABASE_URL}/rest/v1/agent_presence?select=agent_name,status,current_ticket_id,last_heartbeat,assignee_email&workspace_id=eq.${WORKSPACE_ID}`,
      { headers: restHeaders() }
    ),
    _fetchWithTimeout(
      `${SUPABASE_URL}/rest/v1/agent_activity_log?select=ticket_id,kind,agent_name,created_at&workspace_id=eq.${WORKSPACE_ID}&kind=in.(picked_up,shipped)&order=created_at.desc&limit=500`,
      { headers: restHeaders() }
    ),
    // Fetch blocker dependencies for the blocked bucket.
    // Only kind='blocked_by' is needed; workspace_id scoping is via the ticket IDs.
    _fetchWithTimeout(
      `${SUPABASE_URL}/rest/v1/ticket_dependencies?select=ticket_id,depends_on_ticket_id,kind&workspace_id=eq.${WORKSPACE_ID}&kind=eq.blocked_by`,
      { headers: restHeaders() }
    )
  ])

  const presence = presenceRes.ok ? await presenceRes.json() : []
  const activity = activityRes.ok ? await activityRes.json() : []
  const deps = depsRes.ok ? await depsRes.json() : []

  const triage = computeTriage(rows, presence, activity, deps)
  // Expose total so page can decide to lazy-load more.
  triage._totalAvailable = total
  triage._initialLimit = INITIAL_LIMIT
  return triage
}
